import { useState } from 'react';
import { ErrorBoundary } from './lib/bugsnag';
import useCTraderSocket from './hooks/useCTraderSocket';
import usePositionsWithPnl from './hooks/usePositionsWithPnl';
import StatusBadge from './components/StatusBadge';
import AccountStats from './components/AccountStats';
import PriceTicker from './components/PriceTicker';
import EquityChart from './components/EquityChart';
import AllocationChart from './components/AllocationChart';
import PositionsTable from './components/PositionsTable';
import ChartModal from './components/ChartModal';

export default function App({ config, equityHistory }) {
  const { status, trader, positions, prices, closePosition } = useCTraderSocket(config);
  const { positionsWithPnl, totalPnl, totalMargin, equity } = usePositionsWithPnl(positions, prices, trader);
  const [chartInfo, setChartInfo] = useState(null);

  const balance = trader?.balance || 0;
  const freeMargin = equity - totalMargin;
  const leverage = trader?.leverageInCents ? trader.leverageInCents / 100 : null;
  const isLoading = status !== 'connected' || !trader;

  return (
    <ErrorBoundary>
      <StatusBadge status={status} />

      {/* Blur overlay while connecting */}
      <div className={`relative transition-all duration-500 ${isLoading ? 'blur-sm opacity-60 pointer-events-none' : ''}`}>
        <AccountStats
          balance={balance}
          equity={equity}
          totalPnl={totalPnl}
          totalMargin={totalMargin}
          freeMargin={freeMargin}
          positionCount={positionsWithPnl.length}
        />

        <div className="mt-6">
          <PriceTicker prices={prices} positions={positionsWithPnl} onSymbolClick={(sym) => {
            const pos = positionsWithPnl.find(p => p.symbol === sym);
            setChartInfo({ symbol: sym, side: pos?.side });
          }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <EquityChart equity={equity} balance={balance} initialHistory={equityHistory} />
          <AllocationChart positions={positionsWithPnl} />
        </div>

        <div className="mt-6">
          <PositionsTable positions={positionsWithPnl} leverage={leverage} totalPnl={totalPnl} balance={balance} moneyDigits={trader?.moneyDigits} onClose={closePosition} onChart={(sym, side) => setChartInfo({ symbol: sym, side })} />
        </div>
      </div>

      {/* Loading spinner centered over blurred content */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
             style={{ top: '200px' }}>
          <div className="flex flex-col items-center gap-3 bg-base-300/80 rounded-xl px-8 py-6 backdrop-blur-sm">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <span className="text-sm opacity-70">
              {status === 'no-config' ? 'Missing cTrader configuration' :
               status === 'error' ? 'Connection error — retrying...' :
               'Connecting to cTrader...'}
            </span>
          </div>
        </div>
      )}

      {/* TradingView chart modal */}
      <ChartModal symbol={chartInfo?.symbol} side={chartInfo?.side} onClose={() => setChartInfo(null)} />
    </ErrorBoundary>
  );
}
